import type {
  StoryDependencyDisplay,
  StoryEligibility,
  StoryEligibilityInput,
  StoryEligibilityReason,
  StoryPlanView,
  StoryProgress,
  StoryResourceAction,
  StoryResourceGroup,
  StoryResourceTotals,
  StoryRow,
  StoryStatus,
} from '@flowx/designer/modules/ai-builder/models/story-plan.entity'
import { STORY_STATE_CAN_START } from '@flowx/designer/modules/ai-builder/models/story-plan.entity'
import type {
  HarnessStory,
  HarnessStoryDecision,
  HarnessStoryPlanSnapshot,
  HarnessStoryState,
  StoryCollection,
} from '@flowx/designer/modules/ai-builder/types/harness.types'
import { shortCommitId } from '@flowx/designer/modules/ai-builder/utils/write-target.util'

export const STORY_STATUS: Readonly<Record<HarnessStoryState, StoryStatus | null>> = {
  not_started: null,
  implementing: { label: 'implementing', severity: 'info' },
  implemented: { label: 'implemented', severity: 'success' },
  needs_you: { label: 'needs you', severity: 'warn' },
  stopped: { label: 'stopped', severity: 'danger' },
}

/** The plan's way on at a story boundary, past the stories a run already implemented. */
export const IMPLEMENT_REMAINING_LABEL = 'Implement the remaining stories'

export const STORY_RESOURCE_HEADINGS: Readonly<Record<StoryResourceAction, string>> = {
  created: 'Created',
  edited: 'Edited',
  removed: 'Removed',
}

const RESOURCE_ACTIONS: readonly StoryResourceAction[] = ['created', 'edited', 'removed']
const STATE_ELIGIBILITY_REASON: Readonly<Record<HarnessStoryState, StoryEligibilityReason>> = {
  not_started: 'eligible',
  implementing: 'implementing',
  implemented: 'complete',
  needs_you: 'needs_you',
  stopped: 'stopped',
}

/** Whether a reason still renders the story Implement controls, disabled unless eligible: a gate
 * the maker can clear stays in sight; a review on screen, nothing left to implement or an earlier
 * story that needs a run's own way on hides them. */
const STORY_IMPLEMENT_OFFERED: Readonly<Record<StoryEligibilityReason, boolean>> = {
  eligible: true,
  busy: true,
  branch_question: true,
  execution_blocked: true,
  review_open: false,
  unavailable: false,
  empty: false,
  complete: false,
  implementing: false,
  needs_you: false,
  stopped: false,
}

export function storyImplementOffered(eligibility: StoryEligibility | null): boolean {
  return !!eligibility && STORY_IMPLEMENT_OFFERED[eligibility.reason]
}

export function toStoryPlanView(snapshot: HarnessStoryPlanSnapshot): StoryPlanView {
  return {
    snapshot,
    goal: snapshot.goal,
    rows: toStoryRows(snapshot),
    progress: storyProgress(snapshot.stories),
    resourceTotals: storyResourceTotals(snapshot.stories),
  }
}

export function toStoryRows(snapshot: HarnessStoryPlanSnapshot): readonly StoryRow[] {
  const total = storiesComplete(snapshot.stories) ? snapshot.stories.entries.length : null
  return snapshot.stories.entries.map((story) => {
    const groups = storyResourceGroups(story)
    return {
      story,
      position: story.position,
      total,
      status: story.state ? STORY_STATUS[story.state] : null,
      stateAvailable: story.state !== null,
      shortCommit:
        story.state === 'implemented' && story.commit_id ? shortCommitId(story.commit_id) : null,
      acceptance: storyAcceptance(snapshot, story.story_id),
      decisions: storyDecisions(snapshot, story.story_id),
      resourceGroups: groups,
      dependencies: storyDependencies(snapshot.stories, story),
      nothingChanged: story.state === 'implemented' && groups.every((group) => group.count === 0),
    }
  })
}

export function storyProgress(stories: StoryCollection<HarnessStory>): StoryProgress {
  if (!storiesComplete(stories)) {
    return { implemented: null, total: null, allImplemented: false, text: 'Stories unavailable' }
  }
  const total = stories.entries.length
  const implemented = stories.entries.filter((story) => story.state === 'implemented').length
  return {
    implemented,
    total,
    allImplemented: total > 0 && implemented === total,
    text: `${implemented} of ${total} implemented`,
  }
}

export function storyResourceGroups(story: HarnessStory): readonly StoryResourceGroup[] {
  return RESOURCE_ACTIONS.map((action) => ({
    action,
    heading: STORY_RESOURCE_HEADINGS[action],
    resources: story.resources[action],
    count:
      story.resources[action].availability === 'available'
        ? story.resources[action].entries.length
        : null,
  }))
}

/** Story-impact totals, never deduplicated by display name (there is no resource identity). */
export function storyResourceTotals(stories: StoryCollection<HarnessStory>): StoryResourceTotals {
  const count = (action: StoryResourceAction): number | null =>
    storiesComplete(stories) &&
    stories.entries.every((story) => story.resources[action].availability === 'available')
      ? stories.entries.reduce((sum, story) => sum + story.resources[action].entries.length, 0)
      : null
  return { created: count('created'), edited: count('edited'), removed: count('removed') }
}

/** `serves` describes expanded internal steps; only milestone.acceptance owns story checks. */
export function storyAcceptance(
  snapshot: HarnessStoryPlanSnapshot,
  storyId: string
): StoryCollection<string> {
  const contract = snapshot.contract
  if (!contract) {
    return { availability: snapshot.contractAvailability, entries: [] }
  }
  const milestone = contract.milestones.entries.find((entry) => entry.milestone_id === storyId)
  if (!milestone) {
    return {
      availability: contract.milestones.availability === 'missing' ? 'missing' : 'invalid',
      entries: [],
    }
  }
  if (milestone.acceptance.availability === 'missing') {
    return { availability: 'missing', entries: [] }
  }
  if (milestone.acceptance.entries.length && contract.acceptance.availability === 'missing') {
    return { availability: 'missing', entries: [] }
  }
  const statements = milestone.acceptance.entries.map(
    (id) =>
      contract.acceptance.entries.find((entry) => entry.acceptance_id === id)?.statement ?? null
  )
  return {
    availability:
      contract.milestones.availability === 'available' &&
      milestone.acceptance.availability === 'available' &&
      // An explicitly empty milestone needs no statement lookup.
      (!milestone.acceptance.entries.length || contract.acceptance.availability === 'available') &&
      statements.every((statement) => statement !== null)
        ? 'available'
        : 'invalid',
    entries: statements.filter((statement): statement is string => statement !== null),
  }
}

export function storyDecisions(
  snapshot: HarnessStoryPlanSnapshot,
  storyId: string
): StoryCollection<HarnessStoryDecision> {
  const decisions = snapshot.decisions.entries.filter((decision) => decision.story_id === storyId)
  return {
    availability:
      snapshot.decisions.availability === 'available' &&
      decisions.every(
        (decision) =>
          decision.text !== null &&
          decision.chosen !== null &&
          decision.alternatives.availability === 'available'
      )
        ? 'available'
        : snapshot.decisions.availability === 'missing'
          ? 'missing'
          : 'invalid',
    entries: decisions,
  }
}

/** Only explicit earlier-story references are resolved. Names are supplied uses, not guesses. */
export function storyDependencies(
  stories: StoryCollection<HarnessStory>,
  story: HarnessStory
): StoryCollection<StoryDependencyDisplay> {
  const entries = story.dependencies.entries.map((dependency) => {
    const earlier = stories.entries.find(
      (candidate) =>
        candidate.story_id === dependency.story_id && candidate.position < story.position
    )
    const available =
      earlier?.name != null &&
      stories.availability === 'available' &&
      dependency.names.availability === 'available'
    return {
      storyId: dependency.story_id,
      position: earlier?.position ?? null,
      name: earlier?.name ?? null,
      names: dependency.names.entries,
      label: available ? `${earlier.position} · ${earlier.name}` : 'Dependency unavailable',
      available,
    }
  })
  return {
    availability:
      story.dependencies.availability === 'available' && entries.every((entry) => entry.available)
        ? 'available'
        : story.dependencies.availability === 'missing'
          ? 'missing'
          : 'invalid',
    entries,
  }
}

/** The one place the next story's gates are ranked: what the plan holds first, then a review on
 * screen, a press in flight, a blocked execution and the story's own state, and the branch
 * question last, since it only stands between an otherwise eligible story and its review. */
export function nextStoryEligibility(input: StoryEligibilityInput): StoryEligibility {
  const { stories, busy, executionBlocked, reviewOpen, branchQuestion } = input
  if (!storiesComplete(stories)) {
    return { nextStoryId: null, canImplement: false, reason: 'unavailable' }
  }
  const next = stories.entries.find((story) => story.state !== 'implemented')
  if (!next) {
    return {
      nextStoryId: null,
      canImplement: false,
      reason: stories.entries.length ? 'complete' : 'empty',
    }
  }
  const stateReason = next.state ? STATE_ELIGIBILITY_REASON[next.state] : 'unavailable'
  const reason = reviewOpen
    ? 'review_open'
    : busy
      ? 'busy'
      : executionBlocked
        ? 'execution_blocked'
        : stateReason === 'eligible' && branchQuestion
          ? 'branch_question'
          : stateReason
  return {
    nextStoryId: next.story_id,
    canImplement:
      !reviewOpen &&
      !busy &&
      !executionBlocked &&
      !branchQuestion &&
      next.state !== null &&
      STORY_STATE_CAN_START[next.state],
    reason,
  }
}

function storiesComplete(stories: StoryCollection<HarnessStory>): boolean {
  return (
    stories.availability === 'available' && stories.entries.every((story) => story.state !== null)
  )
}
